import React, {useMemo} from 'react';
import {slugifyString} from '@ui/utils/string/slugify-string';
import {Title} from '@app/titles/models/title';
import {
  BaseMediaLink,
  BaseMediaLinkProps,
  getBaseMediaLink,
} from '@app/base-media-link';
import {getEpisodeLink} from '@app/episodes/episode-link';
import {getSeasonLink} from '@app/seasons/season-link';
import {Episode} from '@app/titles/models/episode';
import {CompactSeasonEpisode} from '@app/episodes/compact-season-episode';
import {activeLocale} from '@app/i18n/localize-link';
import {DEFAULT_LOCALE} from '@app/i18n/locale-config';

/**
 * R48 (Day 32) — non-TR aktif locale'de İngilizce slug (slug_en) seç.
 * Path hâlâ "/tr/{film|dizi}/{slug}" olarak kurulur; getBaseMediaLink içindeki
 * localizeTo segmenti aktif dile çevirir (film->movie ...). Sadece slug dile
 * göre değişir. slug_en boşsa TR slug'a fallback (sıfır kırılma).
 */
function pickTitleSlug(title: Title): string | undefined {
  if (activeLocale() !== DEFAULT_LOCALE && title.slug_en) {
    return title.slug_en;
  }
  return title.slug;
}

/**
 * P3 (Day 54) — kart/başlıkta gösterilecek locale-aware ADı seçer.
 *
 * titles.name TR normalize edilmiş ("Susam Sokağı") olduğu için 5 dilde de
 * Türkçe kalıyordu. Non-TR aktif locale'de original_title (gerçek TMDB verisi,
 * ör. "Sesame Street") tercih edilir; boşsa dürüstçe name'e fallback (mock
 * yasak — uydurma çeviri YOK). TR locale = no-op (sıfır regresyon).
 *
 * R5 (5 Tem) — öncelik zinciri genişletildi (BLOK 0 davranışı korunarak):
 *   localized_name (title_translations.name — TMDB per-locale dağıtım adı
 *   sync'i / admin override; TitleLoader payload'a ekler) > original_title >
 *   name(TR). localized_name yoksa davranış P3 ile birebir aynı.
 */
export function getTitleDisplayName(
  title: Pick<Title, 'name' | 'original_title'> & {localized_name?: string},
): string {
  if (activeLocale() !== DEFAULT_LOCALE) {
    const localized = title.localized_name?.trim();
    if (localized) return localized;
    const original = title.original_title?.trim();
    if (original) return original;
  }
  return title.name;
}

interface Props extends Omit<BaseMediaLinkProps, 'link'> {
  title: Title;
}
export function TitleLink({title, children, ...linkProps}: Props) {
  const link = useMemo(() => {
    return getTitleLink(title);
  }, [title]);

  return (
    <BaseMediaLink {...linkProps} link={link}>
      {children ?? getTitleDisplayName(title)}
    </BaseMediaLink>
  );
}

interface WithEpisodeProps extends Props {
  episode: Episode;
}
export function TitleLinkWithEpisodeNumber({
  title,
  episode,
  children,
  ...linkProps
}: WithEpisodeProps) {
  const link = useMemo(() => {
    return getEpisodeLink(title, episode.season_number, episode.episode_number);
  }, [title, episode]);

  return (
    <BaseMediaLink {...linkProps} link={link}>
      {title.name} (<CompactSeasonEpisode episode={episode} />)
    </BaseMediaLink>
  );
}

interface Options {
  absolute?: boolean;
  season?: number | string;
  episode?: number | string;
}

export function getTitleLink(
  title: Title,
  {absolute, season, episode}: Options = {},
): string {
  if (episode && season) {
    return getEpisodeLink(title, season, episode, {absolute});
  } else if (season) {
    return getSeasonLink(title, season, {absolute});
  }
  const locale = 'tr';
  const prefix = title.is_series ? 'dizi' : 'film';
  const slug = pickTitleSlug(title);
  if (slug) {
    return getBaseMediaLink(`/${locale}/${prefix}/${slug}`, {absolute});
  }
  // Fallback for titles without slug (very rare): old route redirects to correct URL
  return getBaseMediaLink(`/icerik/${title.id}/${slugifyString(title.name)}`, {absolute});
}
